import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Search } from 'lucide-react';
import './RocMasterData.css';

const BACKEND_URL = `${process.env.REACT_APP_API_URL || '/api'}/company/search`;

/**
 * Highlight matching text segments (bold the matched parts)
 */
const HighlightText = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const words = query.trim().split(/\s+/).filter(Boolean);
  const regex = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part)
          ? <strong key={i} className="suggestion-highlight">{part}</strong>
          : <span key={i}>{part}</span>
      )}
    </>
  );
};

const RocMasterData = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyData, setCompanyData] = useState(null);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const debounceTimerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Fetch suggestions with debounce
   */
  const fetchSuggestions = useCallback((query) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const response = await axios.get(BACKEND_URL, {
          params: { name: query, limit: 10 }
        });

        if (response.data.success && response.data.data) {
          const results = response.data.data.map(item => ({
            name: item.CompanyName || item.company_name || '',
            cin: item.CIN || item.cin || '',
          })).filter(item => item.name);

          setSuggestions(results);
          setShowSuggestions(results.length > 0);
          setActiveSuggestionIndex(-1);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error('Suggestion fetch error:', err);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchSuggestions(value);
  };

  const handleClear = () => {
    setSearchTerm('');
    setShowResults(false);
    setShowSuggestions(false);
    setSuggestions([]);
    setError('');
    setCompanyData(null);
  };

  /**
   * Select a suggestion and trigger full search
   */
  const handleSelectSuggestion = (suggestion) => {
    setSearchTerm(suggestion.name);
    setShowSuggestions(false);
    setSuggestions([]);
    performSearch(suggestion.name);
  };

  /**
   * Perform the full company search
   */
  const performSearch = async (term) => {
    const query = term || searchTerm;
    if (query.trim() === '') return;

    setLoading(true);
    setShowResults(false);
    setShowSuggestions(false);
    setError('');

    try {
      const response = await axios.get(BACKEND_URL, {
        params: { name: query }
      });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const rawData = response.data.data[0];

        // 🚀 Super-Strong Mapping: તમારી આપેલી યાદી મુજબ બધું જ કવર કરી લીધું છે
        const formattedData = {
          companyName: rawData.CompanyName || rawData.company_name || "N/A",
          cin: rawData.CIN || rawData.cin || "N/A",
          status: rawData.CompanyStatus || rawData.company_status || "Active",
          rocCode: rawData.CompanyROCcode || rawData.roc_code || "N/A",
          registrationNumber: (rawData.CIN || rawData.cin) ? (rawData.CIN || rawData.cin).slice(-6) : "N/A",
          category: rawData.CompanyCategory || rawData.company_category || "N/A",
          subCategory: rawData.CompanySubCategory || rawData.company_subcategory || "-",
          class: rawData.CompanyClass || rawData.class_of_company || "N/A",
          authorizedCapital: rawData.AuthorizedCapital || rawData.authorized_capital || "0",
          paidUpCapital: rawData.PaidupCapital || rawData.paidup_capital || "0",
          dateOfIncorporation: rawData['CompanyRegistrationdate Date'] || rawData.CompanyRegistrationdate_date || "N/A",
          address: rawData['Registered Office Address'] || rawData.Registered_Office_Address || "N/A",
          listingStatus: rawData.Listingstatus || rawData.listing_status || "Unlisted",

          // અહી ભૂલ થતી હતી, હવે બન્ને રીતે ચેક કરશે
          state: rawData.CompanyStateCode || rawData.company_state_code ? (rawData.CompanyStateCode || rawData.company_state_code).toUpperCase() : "N/A",
          industry: rawData.CompanyIndustrialClassification || rawData.company_industrial_classification || "N/A",
          nicCode: rawData['Nic Code'] || rawData.nic_code || "N/A",
          companyType: rawData['CompanyIndian/Foreign Company'] || rawData['Company Indian/Foreign Company'] || "N/A"
        };

        setCompanyData(formattedData);
        setShowResults(true);
      } else {
        setError("Company not found. Please enter the EXACT Full Company Name or CIN.");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Server Error.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Keyboard navigation for suggestions
   */
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') performSearch();
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[activeSuggestionIndex]);
        } else {
          setShowSuggestions(false);
          performSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="roc-page-wrapper">
      <div className="roc-header-section">
        <div className="header-content">
          <h2 className="tool-title">ROC Company Master Data</h2>
          <p className="tool-subtitle">Search by Company Name or CIN/LLPIN</p>

          <div className="compact-search-bar">
            <div className="search-input-wrapper">
              <Search className="search-icon-small" size={18} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Ex: TATA MOTORS LTD"
                className="compact-input"
                value={searchTerm}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (suggestions.length > 0) setShowSuggestions(true);
                }}
                autoComplete="off"
              />
              {searchTerm && (
                <button onClick={handleClear} className="compact-clear-btn">
                  <X size={16} />
                </button>
              )}

              {/* Autocomplete Dropdown */}
              {showSuggestions && (
                <ul className="suggestions-dropdown" ref={suggestionsRef}>
                  {suggestionsLoading ? (
                    <li className="suggestion-loading">Searching...</li>
                  ) : (
                    suggestions.map((item, index) => (
                      <li
                        key={index}
                        className={`suggestion-item ${index === activeSuggestionIndex ? 'suggestion-active' : ''}`}
                        onClick={() => handleSelectSuggestion(item)}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                      >
                        <span className="suggestion-name">
                          <HighlightText text={item.name} query={searchTerm} />
                        </span>
                        {item.cin && (
                          <span className="suggestion-cin">{item.cin}</span>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            <button onClick={() => performSearch()} className="compact-search-btn">
              {loading ? '...' : 'Search'}
            </button>
          </div>

          {error && <p style={{ color: 'red', marginTop: '10px', fontWeight: '500' }}>{error}</p>}
        </div>
      </div>

      <div className="roc-content-area">
        {loading && (
          <div className="loading-state">
            <div className="simple-spinner"></div>
            <span>Fetching records from MCA...</span>
          </div>
        )}

        {!showResults && !loading && !error && (
          <div className="empty-state">
            <p>Enter a CIN or Company Name above to view Master Data.</p>
          </div>
        )}

        {showResults && companyData && (
          <div className="data-card animate-fade">
            <div className="data-header">
              <div>
                <h1 className="company-heading">{companyData.companyName}</h1>
                <span className="cin-tag">CIN: {companyData.cin}</span>
              </div>
              <div className="status-indicator">
                <span className={`dot ${companyData.status === 'Active' ? 'active' : ''}`}></span>
                {companyData.status}
              </div>
            </div>

            <div className="data-body">
              <h4 className="section-heading">Company Details</h4>
              <div className="grid-4">
                <DataItem label="ROC Code" value={companyData.rocCode} />
                <DataItem label="Registration No." value={companyData.registrationNumber} />
                <DataItem label="Category" value={companyData.category} />
                <DataItem label="Sub-Category" value={companyData.subCategory} />
              </div>

              <div className="divider"></div>

              <div className="grid-4">
                <DataItem label="Class" value={companyData.class} />
                <DataItem label="Company Type" value={companyData.companyType} />
                <DataItem label="Incorp. Date" value={companyData.dateOfIncorporation} />
                <DataItem label="State" value={companyData.state} />
              </div>

              <div className="divider"></div>

              <h4 className="section-heading">Financial & Industrial Info</h4>
              <div className="grid-4">
                <DataItem label="Auth. Capital" value={`₹ ${companyData.authorizedCapital}`} bold />
                <DataItem label="Paid-up Capital" value={`₹ ${companyData.paidUpCapital}`} bold />
                <DataItem label="Industry" value={companyData.industry} />
                <DataItem label="NIC Code" value={companyData.nicCode} />
              </div>

              <div className="divider"></div>

              <h4 className="section-heading">Contact & Listing</h4>
              <div className="grid-2">
                <DataItem label="Registered Address" value={companyData.address} fullWidth />
                <DataItem label="Listing Status" value={companyData.listingStatus} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DataItem = ({ label, value, bold, isLink, fullWidth }) => (
  <div className={`data-item ${fullWidth ? 'full-width' : ''}`}>
    <label>{label}</label>
    <span className={`${bold ? 'fw-bold' : ''} ${isLink ? 'text-blue' : ''}`}>
      {value || 'N/A'}
    </span>
  </div>
);

export default RocMasterData;